import {
  type FC,
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ArrowLeft } from "lucide-react";

const COMPACT_HEADER_THRESHOLD_PX = 104;

export const ProgramCompactHeaderSentinel = forwardRef<
  HTMLDivElement,
  { style?: CSSProperties }
>(({ style }, ref) => (
  <div
    ref={ref}
    aria-hidden="true"
    className="pointer-events-none absolute left-0 top-[104px] h-px w-px"
    style={style}
  />
));

ProgramCompactHeaderSentinel.displayName = "ProgramCompactHeaderSentinel";

export const useProgramCompactHeaderVisibility = (): {
  isCompactHeaderVisible: boolean;
  compactHeaderRootRef: RefCallback<HTMLElement>;
  compactHeaderSentinelRef: RefCallback<HTMLDivElement>;
} => {
  const [isCompactHeaderVisible, setIsCompactHeaderVisible] = useState(false);
  const compactHeaderVisibleRef = useRef(false);
  const scrollRootRef = useRef<HTMLElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const setVisible = useCallback((nextIsVisible: boolean) => {
    if (compactHeaderVisibleRef.current === nextIsVisible) {
      return;
    }

    compactHeaderVisibleRef.current = nextIsVisible;
    setIsCompactHeaderVisible(nextIsVisible);
  }, []);

  const cleanup = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  const connect = useCallback(() => {
    cleanup();

    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;

    if (!root || !sentinel) {
      return;
    }

    const syncFromScrollPosition = () => {
      setVisible(root.scrollTop > COMPACT_HEADER_THRESHOLD_PX);
    };

    if (typeof IntersectionObserver === "undefined") {
      root.addEventListener("scroll", syncFromScrollPosition, {
        passive: true,
      });
      cleanupRef.current = () => {
        root.removeEventListener("scroll", syncFromScrollPosition);
      };
      syncFromScrollPosition();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        setVisible(!entry.isIntersecting);
      },
      { root, threshold: 0 },
    );

    observer.observe(sentinel);
    cleanupRef.current = () => observer.disconnect();
    syncFromScrollPosition();
  }, [cleanup, setVisible]);

  const compactHeaderRootRef = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      scrollRootRef.current = node;
      connect();
    },
    [connect],
  );

  const compactHeaderSentinelRef = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      sentinelRef.current = node;
      connect();
    },
    [connect],
  );

  useEffect(() => cleanup, [cleanup]);

  return {
    isCompactHeaderVisible,
    compactHeaderRootRef,
    compactHeaderSentinelRef,
  };
};

interface ProgramCompactHeaderProps {
  isVisible: boolean;
  onBack: () => void;
  title: string;
  details: ReactNode;
  rightLabel: string;
}

export const ProgramCompactHeader: FC<ProgramCompactHeaderProps> = ({
  isVisible,
  onBack,
  title,
  details,
  rightLabel,
}) => (
  <div
    className={`pointer-events-none absolute inset-x-0 top-0 z-40 transform-gpu px-4 pt-[calc(10px+env(safe-area-inset-top))] transition-transform duration-200 will-change-[transform] motion-reduce:transition-none ${
      isVisible ? "visible translate-y-0" : "invisible -translate-y-3"
    }`}
    aria-hidden={!isVisible}
    style={{ transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)" }}
  >
    <div
      className={`relative isolate flex min-h-[62px] items-center gap-3 overflow-hidden rounded-[24px] p-2 pr-3 shadow-[0_16px_38px_rgba(24,24,24,0.12)] ring-1 ring-black/[0.055] ${
        isVisible ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] bg-[#F8F8F3]/94 backdrop-blur-xl" />
      <button
        type="button"
        onClick={onBack}
        aria-label="Назад"
        tabIndex={isVisible ? 0 : -1}
        className="flex size-[46px] shrink-0 items-center justify-center rounded-[18px] bg-white/76 text-[#111111] ring-1 ring-black/[0.045] transition-transform duration-150 ease-out active:scale-[0.96]"
      >
        <ArrowLeft size={23} strokeWidth={2.35} className="shrink-0" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="line-clamp-1 text-[16px] font-black leading-[1.08] tracking-[0] text-[#111111]">
          {title}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-2 text-[12px] font-bold leading-none text-[#4B4B45]">
          {details}
        </div>
      </div>
      <div className="flex h-9 shrink-0 items-center rounded-[14px] bg-[#DDFB1F] px-3 text-[13px] font-black text-[#111111] shadow-[0_10px_22px_rgba(221,251,31,0.2)]">
        {rightLabel}
      </div>
    </div>
  </div>
);
